import { getBudgetAmountAt } from '../../utils/budgetAmounts';
import React, { useState, useMemo, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import {
  Box,
  Typography,
  Divider,
  Button,
  List,
  ListItem,
  ListItemButton,
  Tab,
  Tabs,
  LinearProgress,
  styled,
  useTheme,
  useMediaQuery,
  MenuItem,
  IconButton,
  Menu,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AccountBalance,
  Category,
  ReceiptLong
} from '@mui/icons-material';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SavingsIcon from '@mui/icons-material/Savings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import useBudgetData from '../Budget/useBudgetData';
import { getCurrentNZMonthString } from '../../utils/nzDateUtils';
import { getMetricTrend } from '../../utils/calculationUtils';
import { generateOccurrences } from '../../utils/generateOccurrences';
import PageHeader from '../common/PageHeader';
import MetricCard from '../common/MetricCard';
import SegmentedControl from '../common/SegmentedControl';
import CategoryDonut from './CategoryDonut';
import AiSummaryCard from './AiSummaryCard';
import AppCard from '../common/AppCard';
import EmptyState from '../common/EmptyState';
import CategoryChip from '../common/CategoryChip';
import StatusChip from '../common/StatusChip';
import TransactionDetailDialog from '../common/TransactionDetailDialog';
import TransactionListDialog from '../common/TransactionListDialog';

const AmountTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isPositive'
})(({ theme, isPositive }) => ({
  fontWeight: 600,
  color: isPositive ? theme.palette.success.main : theme.palette.error.main,
}));

// Enhanced progress bar list component with forecast indicators.
// Pass onItemClick to make each row clickable (hover + keyboard affordances).
const ProgressBarList = ({ items, onItemClick }) => {
  return (
    <Box sx={{ width: '100%' }}>
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          <Box
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            role={onItemClick ? 'button' : undefined}
            tabIndex={onItemClick ? 0 : undefined}
            onKeyDown={
              onItemClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onItemClick(item);
                    }
                  }
                : undefined
            }
            sx={{
              py: 1.5,
              ...(onItemClick && {
                cursor: 'pointer',
                px: 1,
                mx: -1,
                borderRadius: 1.5,
                transition: 'background-color 120ms ease',
                '&:hover': { bgcolor: 'grey.100' },
              }),
            }}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5,
              gap: 1,
              minWidth: 0
            }}>
              <Tooltip title={item.name || ''}>
                <Typography
                  variant="body3"
                  noWrap
                  sx={{
                    display: 'block',
                    textTransform: 'capitalize',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {item.name}
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Typography
                  variant="body3"
                  fontWeight="bold"
                  color={item.topRightColor || 'text.primary'}
                  sx={{ ml: 1 }}
                >
                  {item.topRightLabel}
                </Typography>
                {item.badge}
              </Box>
            </Box>
            
            <LinearProgress
              variant="determinate"
              value={Math.min(item.percentage, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                mb: 0.5,
                '& .MuiLinearProgress-bar': {
                  bgcolor: item.barColor,
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                variant="body3" 
                color="text.secondary"
                sx={{ fontSize: '0.8125rem' }}
              >
                {item.bottomLabel}
              </Typography>
            </Box>
        </Box>
          {index < items.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Box>
  );
};

// Shared sizing for the insight cards so Recent / Totals / Budgets / Upcoming
// stay a consistent height, each scrolling internally via AppCard's `scroll`
// mode (uniform custom scroll thumb).
// - sm+: the insights row flexes to absorb all remaining page height, so the
//   four cards always reach the bottom of the viewport. Grid rows are
//   minmax(INSIGHT_CARD_HEIGHT, 1fr): the fixed value is only a floor so the
//   cards never collapse on short windows; each card's full content stays
//   reachable by its own scroll.
// - xs: natural height — a fixed height meant every card scrolled
//   internally, and nested scroll regions are awkward to use with a thumb.
const INSIGHT_CARD_HEIGHT = 480;
// height 0 (not auto/100%) on sm+ so card content can never inflate its grid
// row past the viewport — the row is sized by the grid alone, and minHeight
// stretches the card to fill whatever row height the grid resolves.
const INSIGHT_CARD_SX = {
  height: { xs: 'auto', sm: 0 },
  minHeight: { sm: '100%' },
  display: 'flex',
  flexDirection: 'column'
};
// Inner padding for each scrollable card body (applied to the ScrollArea).
const INSIGHT_SCROLL_SX = { px: 2, pb: 1.5 };

// Main dashboard component
const MainDashboard = () => {
  const { transactions, categories, budgets, getBudgetStatus, accounts, actions } = useData();
  const budgetData = useBudgetData(transactions, categories);
  const [timeRange, setTimeRange] = useState('current-month');
  const [showAllRecent, setShowAllRecent] = useState(false);
  // Transaction clicked in "Recent" (opens the details dialog)
  const [detailTransaction, setDetailTransaction] = useState(null);
  // Budget row clicked in "Budgets" (opens the category transactions + budgets dialog)
  const [budgetDialogItem, setBudgetDialogItem] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0); // 0 = expenses, 1 = income (Totals donut)
  // Totals donut grouping: null = ungrouped (show every category), or a slice
  // cap that collapses the long tail into "Other". Configured via the gear menu.
  // Default to Top 5 + Other on phones, where the full set of slices is too
  // thin to read; wider screens still default to ungrouped.
  const [donutMaxSlices, setDonutMaxSlices] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia('(max-width:599.95px)').matches ? 5 : null)
  );
  const [donutMenuAnchor, setDonutMenuAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const showShortCategoryLabels = useMediaQuery('(min-width:900px) and (max-width: 1150px)');
  const navigate = useNavigate();

  const timeRangeOptions = [
    { value: 'current-month', label: 'Current Month' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'year', label: 'Last 12 Months' },
    { value: 'all', label: 'All Time' }
  ];

  // Label describing the prior period each range is compared against,
  // surfaced in the metric-card trend tooltips.
  const comparisonLabels = {
    'current-month': 'vs last month',
    week: 'vs previous 7 days',
    month: 'vs previous 30 days',
    year: 'vs previous 12 months',
  };

  // Get all category ids marked as transfer
  const transferCategoryIds = useMemo(() =>
    categories.filter(c => c.isTransfer).map(c => c.id),
    [categories]
  );

  // Shared date range calculation function to ensure consistency
  const getDateRangeForTimeRange = useCallback((timeRangeValue) => {
    const now = new Date();
    let startDate, endDate;
    switch (timeRangeValue) {
      case 'current-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        endDate = new Date(now);
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        endDate = new Date(now);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        endDate = new Date(now);
        break;
      default:
        startDate = new Date(0);
        endDate = new Date(now);
    }
    return { startDate, endDate };
  }, []);

  // Filter transactions based on time range
  const filteredTransactions = useMemo(() => {
    const { startDate, endDate } = getDateRangeForTimeRange(timeRange);
    
    const dateFilter = (date) => {
      const txnDate = new Date(date);
      return txnDate >= startDate && txnDate <= endDate;
    };

    return transactions
      .filter(t => !transferCategoryIds.includes(t.category))
      .filter(t => dateFilter(t.date));
  }, [transactions, transferCategoryIds, timeRange, getDateRangeForTimeRange]);

  // Calculate financial metrics
  const financialMetrics = useMemo(() => {
    let income, expenses;

    if (timeRange === 'current-month') {
      // Use useBudgetData for current month — same source of truth as Analysis page
      const currentMonth = getCurrentNZMonthString();
      const monthSummary = budgetData.monthlySummary[currentMonth];
      income = monthSummary?.income || 0;
      expenses = monthSummary?.expenses || 0;
    } else {
      income = filteredTransactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0);

      expenses = filteredTransactions
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    }

    const balance = income - expenses;

    const categorySummary = filteredTransactions.reduce((acc, txn) => {
      const categoryId = txn.category || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = { income: 0, expense: 0 };
      }

      if (txn.amount > 0) {
        acc[categoryId].income += txn.amount;
      } else {
        acc[categoryId].expense += Math.abs(txn.amount);
      }

      return acc;
    }, {});

    // Top categories
    const topIncomeCategories = Object.entries(categorySummary)
      .map(([id, data]) => ({ id, amount: data.income }))
      .filter(c => c.amount > 0)
      .sort((a, b) => b.amount - a.amount);

    const topExpenseCategories = Object.entries(categorySummary)
      .map(([id, data]) => ({ id, amount: data.expense }))
      .filter(c => c.amount > 0)
      .sort((a, b) => b.amount - a.amount);

    // Recent transactions (last 10)
    const recentTransactions = [...filteredTransactions]
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
      income,
      expenses,
      balance,
      recentTransactions,
      topIncomeCategories,
      topExpenseCategories,
      categorySummary
    };
  }, [filteredTransactions, budgetData, timeRange]);

  // Sum income/expenses/balance over an arbitrary date range (transfers excluded),
  // mirroring how filteredTransactions/financialMetrics are derived.
  const computeRangeMetrics = useCallback((startDate, endDate) => {
    const txns = transactions
      .filter(t => !transferCategoryIds.includes(t.category))
      .filter(t => {
        const d = new Date(t.date);
        return d >= startDate && d <= endDate;
      });
    const income = txns.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
    const expenses = txns.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
    return { income, expenses, balance: income - expenses };
  }, [transactions, transferCategoryIds]);

  // Metrics for the period immediately preceding the selected range, used for
  // the period-over-period comparison shown on each metric card. Null for
  // "All Time" (no prior period to compare against).
  const previousMetrics = useMemo(() => {
    if (timeRange === 'all') return null;

    if (timeRange === 'current-month') {
      // Compare against the previous calendar month, using the same
      // budgetData source of truth as the current-month figures.
      const currentMonth = getCurrentNZMonthString();
      const idx = budgetData.months.indexOf(currentMonth);
      const prevMonth = idx > 0 ? budgetData.months[idx - 1] : null;
      const summary = prevMonth ? budgetData.monthlySummary[prevMonth] : null;
      if (!summary) return null;
      return { income: summary.income, expenses: summary.expenses, balance: summary.income - summary.expenses };
    }

    // Rolling windows: shift the current window back by its own length so the
    // prior period sits flush against it (e.g. the 7 days before the last 7).
    const { startDate, endDate } = getDateRangeForTimeRange(timeRange);
    const span = endDate.getTime() - startDate.getTime();
    const prevEnd = new Date(startDate);
    const prevStart = new Date(startDate.getTime() - span);
    return computeRangeMetrics(prevStart, prevEnd);
  }, [timeRange, budgetData, getDateRangeForTimeRange, computeRangeMetrics]);

  // Per-metric trend descriptors for the cards (null when not comparable).
  const metricTrends = useMemo(() => {
    if (!previousMetrics) return {};
    const label = comparisonLabels[timeRange];
    return {
      income: getMetricTrend(financialMetrics.income, previousMetrics.income, true, label),
      expenses: getMetricTrend(financialMetrics.expenses, previousMetrics.expenses, false, label),
      balance: getMetricTrend(financialMetrics.balance, previousMetrics.balance, true, label),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousMetrics, financialMetrics, timeRange]);

  // Net worth = sum of all account balances. This is a point-in-time total and
  // is deliberately independent of the selected time range. Matches the
  // Net Worth figure used elsewhere (liability balances are stored negative).
  const netWorth = useMemo(
    () => (accounts || []).reduce((sum, a) => sum + (a.balance || 0), 0),
    [accounts]
  );

  // Net worth trend: the period's net cash flow (income − expenses) as a share
  // of the net worth at the start of the period — i.e. how much net worth grew
  // over the selected period. Undefined for "All Time" (no comparable prior
  // period) so MetricCard omits the trend slot, matching the other three
  // cards — a null would render the "no comparison yet" placeholder instead.
  const netWorthTrend = useMemo(() => {
    if (!previousMetrics) return undefined;
    return getMetricTrend(netWorth, netWorth - financialMetrics.balance, true, comparisonLabels[timeRange]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [netWorth, financialMetrics.balance, previousMetrics, timeRange]);

  // Calculate budget progress with category-level aggregation
  const budgetProgress = useMemo(() => {
    if (!budgets.length) return [];

    const { startDate, endDate } = getDateRangeForTimeRange(timeRange);

    // Get budget statuses for all active budgets
    const budgetStatuses = budgets
      .filter(budget => budget.isActive)
      .map(budget => getBudgetStatus(budget, startDate, endDate))
      .filter(Boolean);

    // Group by category and aggregate budget amounts only
    const categoryMap = new Map();
    
    budgetStatuses.forEach(status => {
      const categoryId = status.budget.categoryId || 'uncategorized';
      const category = categories.find(c => c.id === categoryId);
      
      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, {
          categoryId,
          categoryName: category?.name || status.budget.name || 'Uncategorized',
          categoryColor: category?.color,
          type: status.budget.type, // Assume all budgets in same category are same type
          totalBudgetAmount: 0,
          budgets: []
        });
      }
      
      const categoryData = categoryMap.get(categoryId);
      categoryData.totalBudgetAmount += status.budgetAmount;
      categoryData.budgets.push(status);
    });

    // Calculate spent amount directly from transactions to avoid duplication
    const categoryBudgets = Array.from(categoryMap.values()).map(cat => {
      // Filter transactions for this category within the date range
      const categoryTransactions = filteredTransactions.filter(txn => 
        txn.category === cat.categoryId
      );
      
      // Calculate spent based on budget type
      const totalSpent = categoryTransactions
        .filter(txn => cat.type === 'Income' ? txn.amount > 0 : txn.amount < 0)
        .reduce((sum, txn) => sum + Math.abs(txn.amount), 0);
      
      const percentage = cat.totalBudgetAmount > 0 ? (totalSpent / cat.totalBudgetAmount) * 100 : 0;
      const isOverBudget = percentage >= 100 && cat.type === 'Expense';
      
      return {
        budget: {
          id: cat.categoryId,
          name: cat.categoryName,
          categoryId: cat.categoryId,
          type: cat.type
        },
        budgetAmount: cat.totalBudgetAmount,
        spent: totalSpent,
        percentage,
        isOverBudget,
        remaining: cat.totalBudgetAmount - totalSpent,
        categoryColor: cat.categoryColor,
        budgetCount: cat.budgets.length,
        budgetStatuses: cat.budgets
      };
    });

    return categoryBudgets.sort((a, b) => b.percentage - a.percentage);
  }, [budgets, timeRange, getBudgetStatus, getDateRangeForTimeRange, categories, filteredTransactions]);

  // Format currency with configurable decimal places
  const formatCurrency = (amount, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(amount);
  };

  // Format date (short form keeps Recent rows on one line)
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // Get category name by id
  const getCategoryName = (categoryId) => {
    if (!categoryId || categoryId === 'uncategorized') return 'Uncategorized';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };


  // Totals donut income/expense toggle
  const handleCategoryTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Totals donut grouping options exposed through the gear menu.
  const donutGroupingOptions = [
    { label: 'Show all (ungrouped)', value: null },
    { label: 'Top 5 + Other', value: 5 },
    { label: 'Top 8 + Other', value: 8 },
    { label: 'Top 10 + Other', value: 10 },
  ];

  // Calculate progress percentages for category bars
  const getCategoryPercentage = (amount, total) => {
    if (total === 0) return 0;
    return (amount / total) * 100;
  };

  // Get current time range label
  const getCurrentTimeRangeLabel = () => {
    const option = timeRangeOptions.find(opt => opt.value === timeRange);
    return option ? option.label.toLowerCase() : timeRange;
  };

  // Format date range for current time range
  const getDateRangeString = () => {
    const { startDate, endDate } = getDateRangeForTimeRange(timeRange);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  };

  // Category breakdown for the Totals donut (expenses or income per the toggle).
  const topCategoriesItems = useMemo(() => {
    const data = selectedTab === 0 ? financialMetrics.topExpenseCategories : financialMetrics.topIncomeCategories;
    return data.map(item => {
      const category = categories.find(c => c.id === item.id) || { name: 'Uncategorized' };
      return { id: item.id, name: category.name, amount: item.amount, color: category.color };
    });
  }, [selectedTab, financialMetrics, categories]);

  // Upcoming recurring bills / budget dates over the next 30 days.
  const upcomingItems = useMemo(() => {
    const today = new Date();
    const winStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const winEnd = new Date(winStart);
    winEnd.setDate(winEnd.getDate() + 30);

    const inWindow = (str) => {
      if (!str) return null;
      const d = new Date(str);
      return (!Number.isNaN(d.getTime()) && d >= winStart && d <= winEnd) ? d : null;
    };

    const nameFor = (categoryId, fallback) =>
      fallback || categories.find(c => c.id === categoryId)?.name || 'Uncategorized';

    const items = [];

    // Actions: skip transfers (not income/expense); expense outflows are negative.
    (actions || [])
      .filter(a => a.isActive && !a.isCompleted && a.type !== 'transfer')
      .forEach(a => {
        const signed = a.type === 'income' ? Math.abs(a.amount) : -Math.abs(a.amount);
        const push = (d, key) => items.push({ id: key, date: d, name: a.name, amount: signed, categoryId: a.categoryId });
        generateOccurrences(a, winStart, winEnd).forEach((d, i) => push(d, `a_${a.id}_${i}`));
      });

    // Budgets: Income positive, Expense negative.
    (budgets || [])
      .filter(b => b.isActive)
      .forEach(b => {
        const name = nameFor(b.categoryId, b.name);
        const push = (d, key) => items.push({ id: key, date: d, name, amount: (b.type === 'Income' ? 1 : -1) * Math.abs(getBudgetAmountAt(b, d)), categoryId: b.categoryId, isBudget: true });
        if (b.isRecurring) {
          generateOccurrences(b, winStart, winEnd).forEach((d, i) => push(d, `b_${b.id}_${i}`));
        } else {
          const d = inWindow(b.startDate);
          if (d) push(d, `b_${b.id}`);
        }
      });

    return items.sort((x, y) => x.date - y.date);
  }, [actions, budgets, categories]);

  const budgetProgressItems = useMemo(() => {
    return budgetProgress.map(item => ({
      id: item.budget.id,
      name: item.budget.name,
      topRightLabel: `${formatCurrency(item.spent, 0)} / ${formatCurrency(item.budgetAmount, 0)}`,
      topRightColor: item.isOverBudget ? 'error.main' : 'text.primary',
      percentage: item.percentage,
      barColor: item.categoryColor || (item.isOverBudget ? 'error.main' : 'success.main'),
      bottomLabel: item.isOverBudget
        // Past ~10× the target a percentage stops being informative
        // ("3343% over budget") — switch to a multiple.
        ? (item.percentage >= 1000
            ? `${Math.round(item.percentage / 100)}× budget`
            : `${Math.round(item.percentage - 100)}% over budget`)
        : `${Math.round(item.percentage)}% used`,
      badge: item.isOverBudget
        ? (
          <Tooltip title="Over budget">
            <WarningAmberRoundedIcon sx={{ fontSize: '1.1rem', color: 'error.main', display: 'block' }} />
          </Tooltip>
        )
        : null,
      type: item.budget.type,
      // Everything the category transactions + budgets dialog needs
      categoryId: item.budget.categoryId || 'uncategorized',
      spent: item.spent,
      budgetAmount: item.budgetAmount,
      remaining: item.remaining,
      isOverBudget: item.isOverBudget,
      categoryColor: item.categoryColor,
      budgetStatuses: item.budgetStatuses,
    }));
  }, [budgetProgress, formatCurrency]);

  // Total uncategorized transactions (drives the review nudge). Deliberately
  // unfiltered by the selected time range, and matching the Rules page filter
  // so the two counts always agree.
  const uncategorized = useMemo(() => {
    const txns = transactions.filter(t => !t.category || t.category === 'uncategorized');
    return {
      count: txns.length,
      total: txns.reduce((sum, t) => sum + Math.abs(t.amount), 0)
    };
  }, [transactions]);

  // Aggregates handed to the AI summary — the same numbers the cards above
  // render, never raw transactions. Values are rounded to whole dollars so the
  // backend's cache hash stays stable against float noise, and lists are
  // capped so the payload stays a couple of KB.
  const aiSummaryData = useMemo(() => {
    const categoryName = (id) =>
      categories.find(c => c.id === id)?.name || 'Uncategorized';
    const trendPct = (trend) => (trend ? Math.round(trend.value) : null);
    return {
      period: {
        label: timeRangeOptions.find(o => o.value === timeRange)?.label,
        comparedTo: comparisonLabels[timeRange] || null,
      },
      totals: {
        netWorth: Math.round(netWorth),
        income: Math.round(financialMetrics.income),
        expenses: Math.round(financialMetrics.expenses),
        net: Math.round(financialMetrics.balance),
        trendPct: {
          netWorth: trendPct(netWorthTrend),
          income: trendPct(metricTrends.income),
          expenses: trendPct(metricTrends.expenses),
          net: trendPct(metricTrends.balance),
        },
      },
      topExpenseCategories: financialMetrics.topExpenseCategories
        .slice(0, 10)
        .map(c => ({ name: categoryName(c.id), amount: Math.round(c.amount) })),
      topIncomeCategories: financialMetrics.topIncomeCategories
        .slice(0, 10)
        .map(c => ({ name: categoryName(c.id), amount: Math.round(c.amount) })),
      budgets: budgetProgress.slice(0, 12).map(b => ({
        name: b.budget.name,
        type: b.budget.type,
        spent: Math.round(b.spent),
        budgeted: Math.round(b.budgetAmount),
        pctUsed: Math.round(b.percentage),
      })),
      uncategorized: {
        count: uncategorized.count,
        total: Math.round(uncategorized.total),
      },
      upcoming30Days: upcomingItems.slice(0, 15).map(u => ({
        name: u.name,
        date: u.date.toISOString().slice(0, 10),
        amount: Math.round(u.amount),
        category: u.categoryId ? categoryName(u.categoryId) : null,
      })),
    };
    // timeRangeOptions/comparisonLabels are per-render literals derived from
    // nothing but timeRange, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeRange, netWorth, financialMetrics, metricTrends, netWorthTrend,
    budgetProgress, uncategorized, upcomingItems, categories,
  ]);

  // Stable range object for the budget dialog (avoids re-filter/reset loops
  // inside TransactionListDialog, which keys effects off this object).
  const selectedDateRange = useMemo(
    () => getDateRangeForTimeRange(timeRange),
    [timeRange, getDateRangeForTimeRange]
  );

  const displayedRecentTransactions = showAllRecent
    ? financialMetrics.recentTransactions
    : financialMetrics.recentTransactions.slice(0, 13);


  // Short labels keep all five options visible in the phone-width pill
  const timeRangeSwitcherOptions = isMobile
    ? timeRangeOptions.map(o => ({
        ...o,
        label: ({
          'current-month': 'This Month',
          'week': '7 Days',
          'month': '30 Days',
          'year': '12 Months',
          'all': 'All',
        })[o.value] || o.label,
      }))
    : timeRangeOptions;

  const timeRangeSwitcher = (
    <SegmentedControl
      options={timeRangeSwitcherOptions}
      value={timeRange}
      onChange={setTimeRange}
      fullWidth={isMobile}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column' },
        gap: 3,
        // Fill the viewport (minus the main area's vertical padding: 24px
        // bottom on xs/sm, plus 24px top from md up) so the insights row
        // below can flex down to the bottom of the page.
        minHeight: { xs: 0, sm: 'calc(100vh - 24px)', md: 'calc(100vh - 48px)' },
      }}
    >
      {/* Five time-range options don't fit beside the title on a phone, so on
          mobile the switcher renders full-width beneath it instead. mb stays 0
          because the page column already provides gap spacing. */}
      <PageHeader
        title="Overview"
        icon={<DashboardCustomizeIcon fontSize="large" />}
        actions={!isMobile && timeRangeSwitcher}
        sx={{ mb: 0 }}
      >
        {isMobile && timeRangeSwitcher}
      </PageHeader>

      {/* Uncategorized transactions nudge — directly under the page header so
          data-quality issues are the first thing seen. */}
      {uncategorized.count > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.warning.main, 0.3),
          }}
        >
          <Category sx={{ color: '#9a5700', fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ color: '#9a5700', flex: 1, minWidth: 200 }}>
            <strong>{formatCurrency(uncategorized.total, 0)}</strong> across {uncategorized.count} transaction{uncategorized.count !== 1 ? 's' : ''} is uncategorized.
          </Typography>
          <Button
            size="small"
            onClick={() => navigate('/rules')}
            endIcon={<ArrowForwardIcon />}
            sx={{
              color: '#9a5700',
              fontWeight: 600,
              flexShrink: 0,
              '&:hover': { bgcolor: (t) => alpha(t.palette.warning.main, 0.15) },
            }}
          >
            Review now
          </Button>
        </Box>
      )}

      {/* Metric cards — Net Worth is point-in-time (independent of the time
          range) and sits alongside the period figures in a row of four. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        gap: { xs: 2, md: 3 }
      }}>
        <Box sx={{ minWidth: 0 }}>
          <MetricCard
            label="Net Worth"
            value={formatCurrency(netWorth, 0)}
            icon={<AccountBalanceWalletIcon />}
            color="primary"
            valueColor="text.primary"
            trend={netWorthTrend}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <MetricCard
            label="Income"
            value={formatCurrency(financialMetrics.income, 0)}
            icon={<ArrowUpwardIcon />}
            color="success"
            trend={metricTrends.income}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            label="Expenses"
            value={formatCurrency(financialMetrics.expenses, 0)}
            icon={<ArrowDownwardIcon />}
            color="error"
            trend={metricTrends.expenses}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            label="Net"
            value={formatCurrency(financialMetrics.balance, 0)}
            icon={<AccountBalance />}
            color={financialMetrics.balance >= 0 ? 'primary' : 'warning'}
            valueColor={financialMetrics.balance >= 0 ? 'primary.main' : 'error.main'}
            trend={metricTrends.balance}
          />
        </Box>
      </Box>

      {/* AI summary of the period — hidden when the period has no activity
          (nothing to say) and self-removes when AI isn't configured. */}
      {filteredTransactions.length > 0 && (
        <AiSummaryCard range={timeRange} data={aiSummaryData} />
      )}

      {/* Insights Section — a single 4-up row on wide desktop (lg+), wrapping
          to 2-up from sm to md and 1 card per row on phones. Sizing per
          breakpoint is documented at INSIGHT_CARD_SX. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
        // Rows never drop below INSIGHT_CARD_HEIGHT, but grow (1fr) to share
        // the leftover viewport height this row absorbs via flex: 1.
        gridAutoRows: { xs: 'auto', sm: `minmax(${INSIGHT_CARD_HEIGHT}px, 1fr)` },
        gap: 3,
        alignItems: 'stretch',
        flex: 1,
        minHeight: 0
      }}>
        {/* Recent Transactions */}
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          <AppCard
            title="Recent"
            sx={INSIGHT_CARD_SX}
            icon={<ListAltIcon />}
            action={
              <Button
                size="small"
                onClick={() => navigate('/transactions')}
                endIcon={<OpenInNewIcon />}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                All
              </Button>
            }
            scroll
            scrollSx={INSIGHT_SCROLL_SX}
          >
              <List disablePadding>
                {displayedRecentTransactions.length > 0 ? (
                  displayedRecentTransactions.map((transaction, index) => (
                    <React.Fragment key={transaction.id || index}>
                      <ListItem disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                          onClick={() => setDetailTransaction(transaction)}
                          sx={{ display: 'block', py: 1, px: 1, mx: -1, borderRadius: 1.5, width: 'auto' }}
                        >
                        {/* Description + amount share one baseline-aligned row */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                          <Tooltip title={transaction.description || ''}>
                            <Typography
                              variant="body3"
                              fontWeight={500}
                              noWrap
                              sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem' }}
                            >
                              {transaction.description}
                            </Typography>
                          </Tooltip>
                          <AmountTypography
                            variant="body3"
                            isPositive={transaction.amount > 0}
                            fontWeight="bold"
                            sx={{ flexShrink: 0, fontSize: '0.875rem' }}
                          >
                            {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount, 2)}
                          </AmountTypography>
                        </Box>
                        {/* Category + pending + date */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 0.5, sm: 1 },
                          flexWrap: 'wrap'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
                            <Tooltip title={getCategoryName(transaction.category)}>
                              <Box component="span" sx={{ display: 'flex', minWidth: 0, flexShrink: 1 }}>
                                <CategoryChip
                                  label={getCategoryName(transaction.category)}
                                  color={categories.find(c => c.id === transaction.category)?.color}
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: 'auto',
                                    py: 0.25,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                      fontSize: '0.75rem',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }
                                  }}
                                />
                              </Box>
                            </Tooltip>
                            {transaction.isPending && (
                              <StatusChip status="warning" label="Pending" sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }} />
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {formatDate(transaction.date)}
                            </Typography>
                          </Box>
                        </Box>
                        </ListItemButton>
                      </ListItem>
                      {index < displayedRecentTransactions.length - 1 && (
                        <Divider component="li" />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <EmptyState
                    dense
                    icon={<ReceiptLong />}
                    title="No transactions yet"
                    description="No transactions found for the selected time period."
                  />
                )}
              </List>
              {financialMetrics.recentTransactions.length > 13 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button size="small" onClick={() => setShowAllRecent(s => !s)}>
                    {showAllRecent ? 'Show Less' : 'Show More'}
                  </Button>
                </Box>
              )}
          </AppCard>
        </Box>

        {/* Category breakdown donut */}
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          <AppCard
            title="In/Out"
            sx={INSIGHT_CARD_SX}
            icon={<Category />}
            action={
              <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 6, p: 0.5 }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleCategoryTabChange}
                  sx={{
                    minHeight: 0,
                    '& .MuiTab-root': { py: 0.75, px: 1, minHeight: 0, minWidth: 0, fontSize: '0.8125rem', borderRadius: 5, color: 'text.secondary' },
                    '& .Mui-selected': { color: selectedTab === 0 ? 'error.main' : 'success.main', bgcolor: 'white' },
                    '& .MuiTabs-indicator': { display: 'none' },
                  }}
                >
                  <Tab icon={<ArrowDownwardIcon sx={{ fontSize: '1rem', color: 'error.main' }} />} aria-label="expenses" />
                  <Tab icon={<ArrowUpwardIcon sx={{ fontSize: '1rem', color: 'success.main' }} />} aria-label="income" />
                </Tabs>
              </Box>
            }
            scroll
            scrollSx={{ ...INSIGHT_SCROLL_SX, pt: 1 }}
          >
              {topCategoriesItems.length > 0 ? (
                <Box>
                  {/* Gear: configure how the Totals breakdown is grouped */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
                    <Tooltip title="Configure breakdown">
                      <IconButton
                        size="small"
                        onClick={(e) => setDonutMenuAnchor(e.currentTarget)}
                        aria-label="Configure totals breakdown"
                        sx={{ color: 'text.secondary' }}
                      >
                        <SettingsIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={donutMenuAnchor}
                      open={Boolean(donutMenuAnchor)}
                      onClose={() => setDonutMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      {donutGroupingOptions.map((opt) => (
                        <MenuItem
                          key={String(opt.value)}
                          selected={donutMaxSlices === opt.value}
                          onClick={() => { setDonutMaxSlices(opt.value); setDonutMenuAnchor(null); }}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                  <CategoryDonut
                    items={topCategoriesItems}
                    total={selectedTab === 0 ? financialMetrics.expenses : financialMetrics.income}
                    formatCurrency={formatCurrency}
                    accentColor={selectedTab === 0 ? 'error.main' : 'success.main'}
                    centerLabel={selectedTab === 0 ? 'Out' : 'In'}
                    maxSlices={donutMaxSlices}
                  />
                </Box>
                ) : (
                  <EmptyState
                    dense
                    icon={<Category />}
                    title={`No ${selectedTab === 0 ? 'expense' : 'income'} data`}
                    description={`No ${selectedTab === 0 ? 'expense' : 'income'} data found for the selected time period.`}
                  />
              )}
          </AppCard>
        </Box>

        {/* Budget Progress */}
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          <AppCard
            title="Budgets"
            sx={INSIGHT_CARD_SX}
            icon={<SavingsIcon />}
            action={
              <Button
                size="small"
                onClick={() => navigate('/cashflow')}
                endIcon={<OpenInNewIcon />}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                All
              </Button>
            }
            scroll
            scrollSx={INSIGHT_SCROLL_SX}
          >
              {budgetProgressItems.length > 0 ? (
                <ProgressBarList items={budgetProgressItems} onItemClick={setBudgetDialogItem} />
              ) : (
                <EmptyState
                  dense
                  icon={<SavingsIcon />}
                  title="No active budgets"
                  description="No active budgets found for the selected time period."
                />
              )}
          </AppCard>
        </Box>

        {/* Upcoming bills / budget dates (next 30 days) */}
        <Box sx={{ minWidth: 0, minHeight: 0 }}>
          <AppCard
            title="Upcoming"
            sx={INSIGHT_CARD_SX}
            icon={<CalendarMonthIcon />}
            action={
              <Button
                size="small"
                onClick={() => navigate('/calendar')}
                endIcon={<OpenInNewIcon />}
                sx={{
                  minWidth: 0,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 5,
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  bgcolor: 'grey.100',
                  '&:hover': { bgcolor: 'grey.200' },
                }}
              >
                Calendar
              </Button>
            }
            scroll
            scrollSx={INSIGHT_SCROLL_SX}
          >
              {upcomingItems.length > 0 ? (
                  <List disablePadding>
                    {upcomingItems.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <ListItem disablePadding sx={{ py: 1, display: 'block' }}>
                          {/* Same two-row layout as "Recent": name + amount share
                              one baseline-aligned row, then chip + date share the
                              meta row — the chip shrinks and ellipsizes instead
                              of dropping to its own line. */}
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                            <Tooltip title={item.name || ''}>
                              <Typography
                                variant="body3"
                                fontWeight={500}
                                noWrap
                                sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem' }}
                              >
                                {item.name}
                              </Typography>
                            </Tooltip>
                            <AmountTypography
                              variant="body3"
                              isPositive={item.amount > 0}
                              fontWeight="bold"
                              sx={{ flexShrink: 0, fontSize: '0.875rem' }}
                            >
                              {item.amount > 0 ? '+' : ''}{formatCurrency(item.amount, 0)}
                            </AmountTypography>
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 0.5, sm: 1 },
                            flexWrap: 'wrap'
                          }}>
                            {/* Category chip (name + colour); plain "Budget"
                                only when a budget has no category to show */}
                            {item.categoryId ? (
                              <Tooltip title={getCategoryName(item.categoryId)}>
                                <Box component="span" sx={{ display: 'flex', minWidth: 0, flexShrink: 1, maxWidth: '100%' }}>
                                  <CategoryChip
                                    label={getCategoryName(item.categoryId)}
                                    color={categories.find(c => c.id === item.categoryId)?.color}
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: 'auto',
                                      py: 0.25,
                                      minWidth: 0,
                                      maxWidth: '100%',
                                      '& .MuiChip-label': {
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            ) : item.isBudget ? (
                              <StatusChip status="neutral" label="Budget" sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }} />
                            ) : null}
                            <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                              {formatDate(item.date)}
                            </Typography>
                          </Box>
                        </ListItem>
                        {index < upcomingItems.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
              ) : (
                <EmptyState
                  dense
                  icon={<CalendarMonthIcon />}
                  title="Nothing upcoming"
                  description="No recurring bills or budget dates in the next 30 days."
                />
              )}
          </AppCard>
        </Box>
      </Box>

      {/* Details for a transaction clicked in "Recent" */}
      <TransactionDetailDialog
        open={Boolean(detailTransaction)}
        transaction={detailTransaction}
        onClose={() => setDetailTransaction(null)}
      />

      {/* Category transactions + budgets for a row clicked in "Budgets" */}
      <TransactionListDialog
        open={Boolean(budgetDialogItem)}
        onClose={() => setBudgetDialogItem(null)}
        categoryId={budgetDialogItem?.categoryId}
        dateRange={selectedDateRange}
        title={budgetDialogItem ? `${budgetDialogItem.name} · ${getCurrentTimeRangeLabel()}` : ''}
        budgetInfo={
          budgetDialogItem && {
            spent: budgetDialogItem.spent,
            budgetAmount: budgetDialogItem.budgetAmount,
            remaining: budgetDialogItem.remaining,
            percentage: budgetDialogItem.percentage,
            isOverBudget: budgetDialogItem.isOverBudget,
            color: budgetDialogItem.categoryColor,
            budgets: budgetDialogItem.budgetStatuses,
          }
        }
      />
    </Box>
  );
};

export default MainDashboard;
